import classNames from 'classnames';
import DatePickerDay from 'components/datepicker/DatePickerDay';
import type { getModifiers } from 'components/datepicker/utils';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import type { DayPickerProps } from 'react-day-picker';
import DayPicker from 'react-day-picker';
import { getDailyBaseCapacityByDate } from 'redux/selectors/utils/capacities';
import type { StartOfWeek } from 'types/account';
import type { RawCapacity } from 'types/summaries';
import { isBetweenDates, isSameMonth } from 'utils/date-helper';
import { fromJSDate } from 'utils/luxon-helpers';
import type { DatePickerMode, DateRange } from './types';

const today = DateTime.now().toISODate();

export interface DayStatusMap {
  [dateString: string]: { status: string | null };
}

interface MemoizedProps extends Omit<DayPickerProps, 'renderDay'> {
  modifiers: ReturnType<typeof getModifiers>;
  mode: DatePickerMode;
  dateRange?: DateRange;
  userCapacity?: Array<RawCapacity>;
  disabledBefore?: string;
  dayStatusMap?: DayStatusMap;
}

const MemoizedDayPicker: React.FC<MemoizedProps> = React.memo(
  ({ mode, userCapacity, disabledBefore, dayStatusMap, ...props }) => {
    const disbledDays = useMemo(() => {
      if (!disabledBefore) return undefined;
      const disabledBeforeDateObj = new Date(disabledBefore);
      return isNaN(disabledBeforeDateObj.getTime()) ? undefined : { before: disabledBeforeDateObj };
    }, [disabledBefore]);

    const proposalModifiers = useMemo(() => {
      if (!dayStatusMap) return {};
      return {
        proposalPending: (day: Date) =>
          dayStatusMap[fromJSDate(day).toISODate()]?.status === 'pending',
        proposalAccepted: (day: Date) =>
          dayStatusMap[fromJSDate(day).toISODate()]?.status === 'accepted',
      };
    }, [dayStatusMap]);

    const mergedModifiers = useMemo(
      () => ({ ...props.modifiers, ...proposalModifiers }),
      [props.modifiers, proposalModifiers]
    );

    const enhancedClassName = classNames(props.className, {
      hasProposalStatuses: !!dayStatusMap,
    });

    return (
      <DayPicker
        {...props}
        className={enhancedClassName}
        modifiers={mergedModifiers}
        disabledDays={disbledDays}
        renderDay={(day, modifiers) => {
          const capacity = userCapacity
            ? getDailyBaseCapacityByDate(userCapacity, day.toISOString().substr(0, 10))
            : null;
          return (
            <DatePickerDay
              day={day}
              modifiers={modifiers}
              startOfWeek={props.firstDayOfWeek! as StartOfWeek}
              today={today}
              capacity={capacity}
            />
          );
        }}
      />
    );
  },
  (prevProps, nextProps) => {
    // rendering multiple date pickers is expensive, especially when updating the range preview on hover
    // so we need to manually compare the props to see if we need to re-render
    const prevModifiers = prevProps.modifiers;
    const nextModifiers = nextProps.modifiers;
    const nextStart = fromJSDate(nextModifiers.start as Date);
    const nextEnd = fromJSDate(nextModifiers.end as Date);
    const prevStart = fromJSDate(prevModifiers.start as Date);
    const prevEnd = fromJSDate(prevModifiers.end as Date);
    const isSameInitialMonth = fromJSDate(prevProps.initialMonth!).hasSame(
      fromJSDate(nextProps.initialMonth!),
      'month'
    );

    const allMarkers = nextModifiers.hoveredWeekMarker.concat(
      nextModifiers.hoveredWeekMarker,
      prevModifiers.hoveredWeekMarker
    );

    const isIncludedInWeekMarkers = allMarkers.find(m => {
      const month = fromJSDate(m as Date).toISODate();
      return (
        isSameMonth(month, fromJSDate(nextProps.month!).toISODate()) ||
        isSameMonth(month, fromJSDate(prevProps.month!).toISODate())
      );
    });

    const isIncludedInModifiers = (
      start: DateTime<true>,
      end: DateTime<true>,
      currentMonth: Date
    ) => {
      const month = fromJSDate(currentMonth);
      return (
        start.hasSame(month, 'month') ||
        end.hasSame(month, 'month') ||
        isBetweenDates(month.startOf('month').toISODate(), start.toISODate(), end.toISODate()) ||
        !isSameInitialMonth
      );
    };

    const hasRangeChanged =
      JSON.stringify(prevProps.dateRange) !== JSON.stringify(nextProps.dateRange);
    const hasDayStatusMapChanged = prevProps.dayStatusMap !== nextProps.dayStatusMap;
    return (
      !isIncludedInModifiers(nextStart, nextEnd, nextProps.month!) &&
      !isIncludedInModifiers(prevStart, prevEnd, prevProps.month!) &&
      !isIncludedInWeekMarkers &&
      !hasRangeChanged &&
      !hasDayStatusMapChanged
    );
  }
);

export default MemoizedDayPicker;
